// eslint-disable-next-line import/no-cycle
import router from '@/router';

// Api
import LocalAuthApi from '@/api/LocalAuthApi';
import UserApi from '@/api/UserApi';
import PermissionApi from '@/api/PermissionApi';

// Constants
// import UserActions from '@/constants/user/actions';

// Utils
import DataUtil from '@/utils/DataUtil';
import ValidationUtil from '@/utils/ValidationUtil';

const actionTypes = {
    login: 'login',
    logout: 'logout',
    loginAndRedirect: 'loginAndRedirect',
    getPermissions: 'getPermissions',
    clearSessionData: 'clearSessionData',
    recordInactivity: 'recordInactivity',
    reset: 'reset',
};
const mutationTypes = {
    LOGIN: 'LOGIN',
    LOGOUT: 'LOGOUT',
    SET_PERMISSIONS_LIST: 'SET_PERMISSIONS_LIST',
    CLEAR_PERMISSIONS: 'CLEAR_PERMISSIONS',
    SET_LOGIN_IN_FLIGHT: 'SET_LOGIN_IN_FLIGHT',
    SET_PERMISSIONS_IN_FLIGHT: 'SET_PERMISSIONS_IN_FLIGHT',
    SET_SELECTED_LOCATION: 'SET_SELECTED_LOCATION',
    SET_INACTIVITY_INFO_TIMER_ID: 'SET_INACTIVITY_INFO_TIMER_ID',
};

const defaults = {
    state: {
        /** @returns { Object } */
        permissions: () => ( {
            /** @type { Object } - a list of permission objects keyed by locationId */
            list: {},
            /** @type { Boolean } */
            inFlight: false,
        } ),
        /** @returns { Object } */
        selectedLocation: () => ( {
            /** @type { Null | Number } */
            id: null,
            /** @type { String } */
            name: '',
            /** @type { String } */
            nameFriendly: '',
            /** @type { Null | Number } */
            websiteId: null,
        } ),
    },
};

export default {
    namespaced: true,
    state: {
        /** @type { Boolean } */
        loginInFlight: false,
        /** @type { Number } */
        accountId: 0,
        /** @type { String } */
        jwtToken: '',
        /** @type { String } */
        baseAuth: '',
        /** @type { String } */
        name: '',
        /** @type { String } */
        password: '',
        /** @type { String } */
        username: '',
        /** @type { Object } */
        permissions: defaults.state.permissions(),
        /** @type { Object } */
        selectedLocation: defaults.state.selectedLocation(),

        /**
         * @type { Object }
         * @property { Number } timerId - a reference to a setTimeout() function
         * @property { Number } maxTimeBeforeLogout - the number of milliseconds before a user is logged out due to inactivity
         */
        inactivityInfo: {
            timerId: null,
            maxTimeBeforeLogout: 60000000, // 60000000 == 1 hour
        },
    },
    getters: {
        /** @returns { Boolean } */
        hasSelectedLocation: ( state ) => ( state.selectedLocation.id !== defaults.state.selectedLocation().id )
            && ( state.selectedLocation.name !== defaults.state.selectedLocation().name )
            && ( state.selectedLocation.websiteId !== defaults.state.selectedLocation().websiteId ),

        /** @returns { Boolean } */
        isLoggedIn: ( state, getters ) => ( state.username !== '' )
            && ( state.password !== '' )
            && ( state.jwtToken !== '' )
            && getters.hasSelectedLocation,

        /** @returns { String } - hopefully the users' first name */
        firstName: ( state ) => state.name.split( ' ' )[ 0 ],

        /** @returns { Array } */
        permissionsList: ( state ) => Object.values( state.permissions.list ),
        /** @returns { Number } */
        permissionsListCount: ( state, getters ) => getters.permissionsList.length,
        /** @returns { Boolean } */
        hasPermissions: ( state, getters ) => ( getters.permissionsListCount > 0 ),
        /** @returns { Function } -> { Array } */
        rolesForLocationId: ( state, getters ) => ( locationId ) => (
            getters.hasPermissions && !!state.permissions.list[ locationId ]
                ? state.permissions.list[ locationId ].authRoleDTOS
                : []
        ),
        /** @returns { Array } */
        rolesForCurrentLocation: ( state, getters, rootState, rootGetters ) => (
            rootGetters[ 'Website/hasInfo' ]
                ? getters.rolesForLocationId( rootState.Website.info.locationId )
                : []
        ),
        /** @returns { Function } -> { Boolean } */
        rolesForCurrentLocationHasRole: ( state, getters ) => ( role ) => (
            getters.rolesForCurrentLocation
                .map( ( locationRole ) => locationRole.name )
                .includes( role )
        ),
        /** @returns { Function } -> { Array } */
        actionsForLocationId: ( state, getters ) => ( locationId ) => (
            getters.hasPermissions && !!state.permissions.list[ locationId ]
                ? getters.rolesForLocationId( locationId ).flatMap( ( location ) => location.actions )
                : []
        ),
        /** @returns { Array } */
        actionsForCurrentLocation: ( state, getters, rootState, rootGetters ) => (
            rootGetters[ 'Website/hasInfo' ]
                ? [ ...new Set( getters.actionsForLocationId( rootState.Website.info.locationId ) ) ]
                : []
        ),
        /** @returns { Function } -> { Boolean } */
        actionsForCurrentLocationHasAction: ( state, getters ) => ( action ) => (
            getters.actionsForCurrentLocation.includes( action )
        ),
    },
    actions: {
        async login(context, { username, password }) {
            context.commit('SET_LOGIN_IN_FLIGHT', { boolean: true });
            await context.dispatch('clearSessionData');
            try {
                const { data } = await UserApi.login({ username, password });
                context.commit('LOGIN', {
                    jwtToken: data.jwtToken,
                    username: data.userName,
                    password,
                    name: data.name,
                    userAccountId: data.userAccountID,
                    baseAuth: DataUtil.getBase64AuthString(username, password),
                });
                await context.dispatch('getPermissions', { username: data.userName });
                await context.dispatch('recordInactivity');
            } catch (error) {
                console.error('Error logging in: ', error);
                // Route to Login
                if (router.history.current.path !== '/login') {
                    router.push({ path: '/login' }).catch((stupidError) => {
                        console.error('Login failure - redirect failure: ', stupidError);
                    });
                }
                throw error;
            } finally {
                context.commit('SET_LOGIN_IN_FLIGHT', { boolean: false });
            }
        },

        /**
         * Log the user in and redirect to a provided path upon success
         * @params Object { username, password, path }
         */
        async [ actionTypes.loginAndRedirect ]( context, { username, password, path = '/' } ) {
            try {
                await context.dispatch( actionTypes.login, { username, password } );
                if ( router.history.current.path !== path ) {
                    router.push( { path } ).catch( ( stupidError ) => {
                        console.error( 'loginAndRedirect failure - redirect failure: ', stupidError );
                    } );
                }
                // router.push( { path } );
            } catch ( error ) {
                // Throw error
                console.error( 'error: ', error );
                throw error;
            }
        },

        async [ actionTypes.logout ]( context ) {
            await context.dispatch( actionTypes.clearSessionData );
            context.commit( mutationTypes.LOGOUT );
        },

        /**
         * Get a list of the user's Permissions
         */
        async [ actionTypes.getPermissions ]( context, { username } ) {
            context.commit( mutationTypes.SET_PERMISSIONS_IN_FLIGHT, { boolean: true } );
            try {
                const { data } = await PermissionApi.getUserMailPermissions( {
                    jwtToken: context.state.jwtToken,
                    userAccountId: context.state.accountId,
                    username,
                } );

                context.commit( mutationTypes.SET_PERMISSIONS_LIST, {
                    permissionsList: ValidationUtil.arrayHasItems( data.locationPermissionsList )
                        ? DataUtil.normalize( data.locationPermissionsList, 'locationId' )
                        : {},
                } );

                // Set state.selectedLocation if user only has permissions to one location, then get info for that site
                if ( ValidationUtil.arrayHasItems( data.locationPermissionsList ) && data.locationPermissionsList.length === 1 ) {
                    await context.dispatch( 'setUserLocationGetWebsiteInfoGoHome', {
                        locationId: data.locationPermissionsList[ 0 ].locationId,
                        locationName: data.locationPermissionsList[ 0 ].locationName,
                        locationNameFriendly: data.locationPermissionsList[ 0 ].friendly,
                        websiteId: data.locationPermissionsList[ 0 ].websiteId,
                    }, { root: true } );
                } else if ( LocalAuthApi.hasLocalLocation() ) {
                    await context.dispatch( 'setUserLocationGetWebsiteInfoGoHome', {
                        locationId: LocalAuthApi.getLocalLocation().id,
                        locationName: LocalAuthApi.getLocalLocation().name,
                        locationNameFriendly: LocalAuthApi.getLocalLocation().nameFriendly,
                        websiteId: LocalAuthApi.getLocalLocation().websiteId,
                    }, { root: true } );
                }
            } catch ( error ) {
                console.error( 'Error getting permissions: ', error );
                context.commit( mutationTypes.SET_PERMISSIONS_LIST, { permissionsList: {} } );
                throw error;
            } finally {
                context.commit( mutationTypes.SET_PERMISSIONS_IN_FLIGHT, { boolean: false } );
            }
        },

        [ actionTypes.clearSessionData ]( { commit, dispatch } ) {
            commit( mutationTypes.CLEAR_PERMISSIONS );
            // Clear other things here across the application as needed
            dispatch( 'reset', {}, { root: true } );
            dispatch( 'Website/reset', {}, { root: true } );
            dispatch( 'Mail/Delivery/reset', {}, { root: true } );
            dispatch( 'Mail/Delivery/Current/reset', {}, { root: true } );
            dispatch( 'Mail/Pickup/reset', {}, { root: true } );
            dispatch( 'Mail/Search/reset', {}, { root: true } );
            dispatch( 'Mail/Settings/reset', {}, { root: true } );
            dispatch( 'Mail/Shelve/reset', {}, { root: true } );
            dispatch( 'Mail/reset', {}, { root: true } );
            dispatch( 'Inventory/JoinPhysical/reset', {}, { root: true } );
            dispatch( 'Inventory/PartnerShip/reset', {}, { root: true } );
            dispatch( 'Inventory/Shelve/reset', {}, { root: true } );
            dispatch( 'Inventory/reset', {}, { root: true } );
        },

        /**
         * Record user inactivity, and once they're over 'inactivityInfo.maxTimeBeforeLogout', dispatch logout
         * Inspiration from: https://stackoverflow.com/a/10126042
         */
        async [ actionTypes.recordInactivity ]( context ) {
            async function logout() {
                await context.dispatch( actionTypes.logout );
            }
            function resetTimer() {
                clearTimeout( context.state.inactivityInfo.timerId );
                context.commit( mutationTypes.SET_INACTIVITY_INFO_TIMER_ID, {
                    number: setTimeout( logout, context.state.inactivityInfo.maxTimeBeforeLogout ),
                } );
            }
            // Init
            window.addEventListener( 'load', resetTimer, true );
            // DOM Events - an array of events that will reset the inactivity timer
            const capturedEventList = [
                'mousedown',
                'mousemove',
                'keypress',
                'scroll',
                'touchstart',
                'click',
            ];
            capturedEventList.forEach( ( event ) => {
                document.addEventListener( event, resetTimer, true );
            } );
        },
    },
    mutations: {
        LOGIN(state, {
            jwtToken, username, password, name, userAccountId, baseAuth,
        }) {
            state.name = name;
            state.accountId = userAccountId;
            state.username = username;
            state.password = password;
            state.jwtToken = jwtToken;
            state.baseAuth = baseAuth;
            LocalAuthApi.setLocalUsername(username);
            LocalAuthApi.setLocalPassword(password);
        },

        [ mutationTypes.LOGOUT ]( state ) {
            state.name = '';
            state.accountId = 0;
            state.username = '';
            state.password = '';
            state.jwtToken = '';
            state.permissions = defaults.state.permissions();
            state.selectedLocation = defaults.state.selectedLocation();
            LocalAuthApi.removeAllLocalCredentials();
            // Route to the logout page when this mutation is committed
            router.push( { path: '/login' } )
                .catch( ( error ) => {
                    if ( error.name !== 'NavigationDuplicated' ) {
                        console.error( 'Error navigating: ', error );
                    }
                } );
        },

        [ mutationTypes.SET_LOGIN_IN_FLIGHT ]( state, { boolean } ) {
            state.loginInFlight = boolean;
        },

        [ mutationTypes.SET_PERMISSIONS_LIST ]( state, { permissionsList } ) {
            state.permissions.list = permissionsList;
        },
        [ mutationTypes.SET_PERMISSIONS_IN_FLIGHT ]( state, { boolean } ) {
            state.permissions.inFlight = boolean;
        },
        [ mutationTypes.CLEAR_PERMISSIONS ]( state ) {
            state.permissions = defaults.state.permissions();
        },
        [ mutationTypes.SET_SELECTED_LOCATION ]( state, {
            id,
            name,
            nameFriendly,
            websiteId,
        } ) {
            const location = {
                id: id || defaults.state.selectedLocation().id,
                name: name || defaults.state.selectedLocation().name,
                nameFriendly: nameFriendly || defaults.state.selectedLocation().nameFriendly,
                websiteId: websiteId || defaults.state.selectedLocation().websiteId,
            };
            state.selectedLocation = location;
            LocalAuthApi.setLocalLocation( location );
        },
        [ mutationTypes.SET_INACTIVITY_INFO_TIMER_ID ]( state, { number } ) {
            state.inactivityInfo.timerId = number;
        },
    },
};
